import React, { useState, useEffect, useRef } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Menu, X, ChevronDown } from "lucide-react";
import { services } from "../data/services";

const primaryLinks = [
  { to: "/", label: "Home" },
  { to: "/about", label: "About" },
  { to: "/services", label: "Services", hasDropdown: true },
  { to: "/contact", label: "Contact" },
];

export default function Nav() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [mobileServicesOpen, setMobileServicesOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const dropdownRef = useRef(null);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 30);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
    setDropdownOpen(false);
    setMobileServicesOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    const onClick = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const isActive = (to) => {
    if (to === "/") return location.pathname === "/";
    return location.pathname.startsWith(to);
  };

  return (
    <header
      data-testid="site-nav"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled || location.pathname !== "/" ? "bg-[#05101f]/90 backdrop-blur-xl border-b border-white/5" : ""
      }`}
    >
      <div className="max-w-[1280px] mx-auto px-6 py-3.5 flex items-center justify-between gap-6">
        <Link to="/" data-testid="nav-logo" className="flex items-center gap-3 shrink-0 no-underline">
          <img src="/images/logo.jpg" alt="EmirATech Distribution" className="w-11 h-11 rounded-lg object-cover" />
          <div className="hidden sm:flex flex-col leading-tight">
            <span className="text-white font-semibold text-[15px]" style={{ fontFamily: "Sora, sans-serif" }}>
              EmirATech
            </span>
            <span className="text-[10px] uppercase tracking-[0.18em] text-[#7fa5e6]">
              Distribution L.L.C-FZ
            </span>
          </div>
        </Link>

        <nav className="hidden lg:flex items-center gap-1">
          {primaryLinks.map((l) => {
            if (l.hasDropdown) {
              return (
                <div key={l.to} ref={dropdownRef} className="relative">
                  <button
                    data-testid="nav-services-toggle"
                    onClick={() => setDropdownOpen(!dropdownOpen)}
                    onMouseEnter={() => setDropdownOpen(true)}
                    className={`nav-link inline-flex items-center gap-1 ${isActive(l.to) ? "text-white bg-white/5" : ""}`}
                  >
                    {l.label}
                    <ChevronDown size={14} className={`transition-transform ${dropdownOpen ? "rotate-180" : ""}`} />
                  </button>
                  {dropdownOpen && (
                    <div
                      data-testid="nav-services-dropdown"
                      onMouseLeave={() => setDropdownOpen(false)}
                      className="absolute top-full left-0 mt-2 w-[520px] p-3 rounded-xl grid grid-cols-2 gap-1"
                      style={{ background: "rgba(10,26,48,0.98)", backdropFilter: "blur(20px)", border: "1px solid rgba(255,255,255,0.08)", boxShadow: "0 20px 40px rgba(0,0,0,0.4)" }}
                    >
                      <Link
                        to="/services"
                        data-testid="nav-dropdown-all"
                        className="col-span-2 flex items-center justify-between px-3 py-2.5 rounded-lg text-white hover:bg-white/5 no-underline"
                        style={{ fontFamily: "Sora, sans-serif", fontSize: 13, fontWeight: 600 }}
                      >
                        <span>All services</span>
                        <span className="text-[#7fa5e6]">→</span>
                      </Link>
                      <div className="col-span-2 h-px bg-white/5 my-1" />
                      {services.map((s, i) => (
                        <Link
                          key={s.slug}
                          to={`/services/${s.slug}`}
                          data-testid={`nav-dropdown-${i}`}
                          className="flex items-start gap-3 p-3 rounded-lg hover:bg-white/5 no-underline transition"
                        >
                          <div className={`svc-icon ${s.iconCls} shrink-0`} style={{ width: 36, height: 36 }}>{s.icon}</div>
                          <div className="min-w-0">
                            <div className="text-white text-[13.5px] font-semibold truncate" style={{ fontFamily: "Sora, sans-serif" }}>{s.title}</div>
                            <div className="text-[#a6b3c8] text-[11.5px] mt-0.5 truncate">{s.tagline}</div>
                          </div>
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
              );
            }
            return (
              <Link
                key={l.to}
                to={l.to}
                data-testid={`nav-link-${l.label.toLowerCase()}`}
                className={`nav-link no-underline ${isActive(l.to) ? "text-white bg-white/5" : ""}`}
              >
                {l.label}
              </Link>
            );
          })}
        </nav>

        <button
          data-testid="nav-cta-contact"
          onClick={() => navigate("/contact")}
          className="hidden lg:inline-flex btn-primary"
          style={{ padding: "10px 20px", fontSize: 14 }}
        >
          Get in Touch
        </button>

        <button
          data-testid="nav-mobile-toggle"
          className="lg:hidden text-white p-2"
          onClick={() => setOpen(!open)}
          aria-label="Toggle menu"
        >
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      {open && (
        <div data-testid="nav-mobile-menu" className="lg:hidden bg-[#05101f]/95 backdrop-blur-xl border-t border-white/5 px-6 py-5 flex flex-col gap-1 max-h-[85vh] overflow-y-auto">
          {primaryLinks.map((l) => {
            if (l.hasDropdown) {
              return (
                <div key={l.to}>
                  <button
                    data-testid="mobile-nav-services-toggle"
                    onClick={() => setMobileServicesOpen(!mobileServicesOpen)}
                    className="nav-link w-full flex items-center justify-between text-base py-3"
                  >
                    {l.label}
                    <ChevronDown size={16} className={`transition-transform ${mobileServicesOpen ? "rotate-180" : ""}`} />
                  </button>
                  {mobileServicesOpen && (
                    <div className="pl-3 flex flex-col gap-1 mb-2">
                      <Link to="/services" className="nav-link text-sm py-2 no-underline">All services →</Link>
                      {services.map((s) => (
                        <Link key={s.slug} to={`/services/${s.slug}`} className="nav-link text-sm py-2 no-underline">
                          {s.title}
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
              );
            }
            return (
              <Link key={l.to} to={l.to} className="nav-link text-base py-3 no-underline">
                {l.label}
              </Link>
            );
          })}
          <button
            data-testid="mobile-nav-cta"
            onClick={() => navigate("/contact")}
            className="btn-primary mt-3 justify-center"
          >
            Get in Touch
          </button>
        </div>
      )}
    </header>
  );
}
